import {CheckModel} from './Tree.consts'
import type {Node, NodeValue} from './Tree.types'

export type FlatNode = Node & {
  children?: ReadonlyArray<Node>
  treeDepth?: number
  isChild: boolean
  isParent: boolean
  isLeaf: boolean
  checked?: boolean
  expanded?: boolean
  parent?: Node
  index: number
  checkState?: number
}
type FlatNodes = Record<NodeValue, FlatNode>

class TreeModel {
  flatNodes: FlatNodes = {}
  expandOnlyOneLeaf = true
  constructor(nodes: FlatNodes = {}, expandOnlyOneLeaf = true) {
    this.flatNodes = nodes
    this.expandOnlyOneLeaf = expandOnlyOneLeaf
  }

  clone(): TreeModel {
    const clonedNodes: FlatNodes = {}
    // Re-construct nodes one level deep to avoid shallow copy of mutable characteristics
    Object.keys(this.flatNodes).forEach(value => {
      const node = this.flatNodes[value]
      clonedNodes[value] = {...node}
    })
    return new TreeModel(clonedNodes, this.expandOnlyOneLeaf)
  }

  getNode(value: NodeValue): FlatNode | null | undefined {
    return this.flatNodes[value]
  }

  flattenNodes(nodes: ReadonlyArray<Node> | null | undefined, parent?: Node, depth = 0): void {
    if (!Array.isArray(nodes) || nodes.length === 0) {
      return
    }

    // Flatten the `node` property for internal lookups
    nodes.forEach((node, index) => {
      const isParent = this.nodeHasChildren(node)
      this.flatNodes[node.value] = {
        label: node.label,
        value: node.value,
        children: node.children,
        index,
        parent,
        isChild: parent != null,
        isParent,
        isLeaf: !isParent,
        treeDepth: depth,
      }
      this.flattenNodes(node.children, node, depth + 1)
    })
  }

  nodeHasChildren(node: Node): boolean {
    return Array.isArray(node.children)
  }

  deserializeLists(lists: {
    checked: ReadonlyArray<NodeValue>
    expanded: ReadonlyArray<NodeValue>
  }): void {
    const listKeys = ['checked', 'expanded'] as const
    // Reset values to false
    Object.keys(this.flatNodes).forEach(value => {
      listKeys.forEach(listKey => {
        this.flatNodes[value][listKey] = false
      })
    })
    // Deserialize values and set their nodes to true
    listKeys.forEach(listKey => {
      lists[listKey].forEach(value => {
        if (this.flatNodes[value] !== undefined) {
          this.flatNodes[value][listKey] = true
        }
      })
    })
  }

  serializeList(key: keyof FlatNode): ReadonlyArray<string> {
    const list: string[] = []
    Object.keys(this.flatNodes).forEach(value => {
      if (this.flatNodes[value][key]) {
        list.push(value)
      }
    })
    return list
  }

  expandAllNodes(expand: boolean): TreeModel {
    Object.keys(this.flatNodes).forEach(value => {
      if (this.flatNodes[value].isParent) {
        this.flatNodes[value].expanded = expand
      }
    })
    return this
  }

  toggleChecked(
    node: FlatNode | null | undefined,
    isChecked: boolean,
    checkModel: CheckModel,
    percolateUpward = true,
  ): TreeModel {
    if (node != null) {
      const flatNode = this.flatNodes[node.value]
      const modelHasParents = [CheckModel.PARENT, CheckModel.ALL].includes(checkModel)
      const modelHasLeaves = [CheckModel.LEAF, CheckModel.ALL].includes(checkModel)

      if (flatNode.isLeaf) {
        this.checkNode(node.value, isChecked)
      } else {
        if (modelHasParents) {
          this.checkNode(node.value, isChecked)
        }

        if (modelHasLeaves && Array.isArray(flatNode.children)) {
          // Percolate check status down to all children
          flatNode.children.forEach(child => {
            this.toggleChecked(this.getNode(child.value), isChecked, checkModel, false)
          })
        }
      }

      // Percolate check status up to parent
      // The check model must include parent nodes and we must not have already covered the
      // parent (relevant only when percolating through children)
      if (percolateUpward && flatNode.isChild && modelHasParents && flatNode.parent != null) {
        this.toggleParentStatus(flatNode.parent, checkModel)
      }
    }

    return this
  }

  checkAllNodes(isChecked: boolean, checkModel: CheckModel): TreeModel {
    Object.keys(this.flatNodes).forEach(value =>
      this.toggleChecked(this.flatNodes[value], isChecked, checkModel),
    )
    return this
  }

  toggleParentStatus(node: Node | null | undefined, checkModel: CheckModel): void {
    if (node != null) {
      const {value} = node
      const flatNode = this.flatNodes[value]

      if (flatNode.isChild && flatNode.parent != null) {
        if (checkModel === CheckModel.ALL) {
          this.checkNode(value, this.isEveryChildChecked(flatNode))
        }

        this.toggleParentStatus(flatNode.parent, checkModel)
      } else {
        this.checkNode(value, this.isEveryChildChecked(flatNode))
      }
    }
  }

  isEveryChildChecked(node: FlatNode): boolean {
    return Array.isArray(node.children)
      ? node.children.every(child => this.getNode(child.value)?.checked === true)
      : true
  }

  expandNode(value: NodeValue, toggleValue: boolean): TreeModel {
    if (this.expandOnlyOneLeaf) {
      const leaves = this.serializeList('isLeaf')
      leaves.forEach(leafId => (this.flatNodes[leafId].expanded = false))
    }
    this.flatNodes[value].expanded = toggleValue
    return this
  }

  checkNode(value: NodeValue, toggleValue: boolean): TreeModel {
    this.flatNodes[value].checked = toggleValue
    return this
  }
}

export default TreeModel
